import { useEffect, useMemo, useState } from "react";
import { soulManager } from "../../data/managers/SoulManager";
import { SoulDataModel } from "../../data/models/Soul/SoulDataModel";

export function useAdminUsers() {
  const [users, setUsers] = useState<SoulDataModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof SoulDataModel>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const perPage = 10;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await soulManager.getAllSouls();
      setUsers(res!);
    } catch (e) {
      console.error("❌ Error cargando usuarios:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const lower = search.toLowerCase();
    return users.filter(
      (u) =>
        u.fullName?.toLowerCase().includes(lower) ||
        u.email.toLowerCase().includes(lower) ||
        u.passport!.toLowerCase().includes(lower)
    );
  }, [users, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return 0;
    });
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const handleSort = (field: keyof SoulDataModel) => {
    if (field === sortBy) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const handleExportCSV = () => {
    const csv = [
      ["ID", "Nombre", "Correo", "Rol", "Plan", "Pasaporte", "Creado"],
      ...users.map((u) => [
        u.userId,
        u.fullName,
        u.email,
        u.role,
        u.passport,
        u.createdAt,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "usuarios.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    users: sorted,
    loading,
    search,
    setSearch,
    sortBy,
    sortDir,
    handleSort,
    page,
    setPage,
    totalPages,
    handleExportCSV,
  };
}
